// Same regex used in LoginForm + the per-row form: permissive but
// spec-aligned enough to catch the obvious typos.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ParsedLine {
  lineNumber: number;
  email: string;
  name?: string;
  invalid?: string;
}

/**
 * Parse one line into { email, name? }. Accepted delimiters between the
 * email and the (optional) name: tab, comma, `>`, or one-or-more spaces.
 * The first match wins, so a name that itself contains spaces is
 * preserved as-is once the first separator is consumed.
 *
 * Examples:
 *   john@x.com<TAB>Matthew       → email: john@x.com, name: "Matthew"
 *   john@x.com,John Doe     → email: john@x.com, name: "John Doe"
 *   john@x.com > John Doe   → email: john@x.com, name: "John Doe"
 *   john@x.com John Doe     → email: john@x.com, name: "John Doe"
 *   john@x.com                   → email: john@x.com, name: undefined
 */
export function parseLine(raw: string, lineNumber: number): ParsedLine | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\S+?)(?:\s*[\t,>]\s*|\s+)(.*)$/);
  let email: string;
  let name: string | undefined;
  if (match) {
    email = match[1];
    name = match[2].trim() || undefined;
  } else {
    email = trimmed;
  }
  if (!EMAIL_RE.test(email)) {
    return { lineNumber, email, name, invalid: email };
  }
  return { lineNumber, email, name };
}

export function parseBulk(raw: string): ParsedLine[] {
  return raw
    .split(/\r?\n/)
    .map((line, idx) => parseLine(line, idx + 1))
    .filter((p): p is ParsedLine => p !== null);
}
